import type { StarId } from './domain.js';
import { stableStarIdFromZhTW } from './starRegistry.js';

export type FormalStarCatalogGroup =
  | 'major'
  | 'minor'
  | 'adjective'
  | 'changsheng12'
  | 'boshi12'
  | 'jiangqian12'
  | 'suiqian12';

export interface FormalStarCatalogEntry {
  id: StarId;
  label: string;
  groups: readonly FormalStarCatalogGroup[];
}

export interface FormalStarCatalog {
  id: 'IZTRO-DEFAULT-108';
  version: '2.6.0-1';
  engine: 'iztro@2.6.0';
  algorithm: 'default';
  entries: readonly FormalStarCatalogEntry[];
  placementCount: 114;
  uniqueStarCount: 108;
  sourceNote: string;
}
const GROUP_LABELS: Readonly<Record<FormalStarCatalogGroup, readonly string[]>> = Object.freeze({
  major: Object.freeze(['紫微','天機','太陽','武曲','天同','廉貞','天府','太陰','貪狼','巨門','天相','天梁','七殺','破軍']),
  minor: Object.freeze(['左輔','右弼','文昌','文曲','天魁','天鉞','祿存','天馬','擎羊','陀羅','火星','鈴星','地空','地劫']),
  adjective: Object.freeze([
    '解神','三台','天壽','天巫','天廚','陰煞','天哭','天刑','封誥','華蓋','天喜','天空','孤辰',
    '鳳閣','天福','截路','蜚廉','年解','天姚','空亡','龍池','台輔','旬空','咸池','天貴','月德',
    '天才','天虛','紅鸞','恩光','天官','天月','天傷','八座','天德','寡宿','破碎','天使',
  ]),
  changsheng12: Object.freeze(['長生','沐浴','冠帶','臨官','帝旺','衰','病','死','墓','絕','胎','養']),
  boshi12: Object.freeze(['博士','力士','青龍','小耗','將軍','奏書','飛廉','喜神','病符','大耗','伏兵','官府']),
  jiangqian12: Object.freeze(['將星','攀鞍','歲驛','息神','華蓋','劫煞','災煞','天煞','指背','咸池','月煞','亡神']),
  suiqian12: Object.freeze(['歲建','晦氣','喪門','貫索','官符','小耗','大耗','龍德','白虎','天德','弔客','病符']),
});

const GROUP_ORDER: readonly FormalStarCatalogGroup[] = Object.freeze([
  'major','minor','adjective','changsheng12','boshi12','jiangqian12','suiqian12',
]);
function buildEntries(): readonly FormalStarCatalogEntry[] {
  const groupsByLabel = new Map<string, FormalStarCatalogGroup[]>();
  for (const group of GROUP_ORDER) {
    for (const label of GROUP_LABELS[group]) {
      const groups = groupsByLabel.get(label) ?? [];
      if (!groups.includes(group)) groups.push(group);
      groupsByLabel.set(label, groups);
    }
  }
  return Object.freeze([...groupsByLabel.entries()].map(([label, groups]) => Object.freeze({
    id: stableStarIdFromZhTW(label),
    label,
    groups: Object.freeze([...groups]),
  })));
}

const ENTRIES = buildEntries();
if (ENTRIES.length !== 108) throw new Error(`Formal iztro default star catalog expected 108 unique stars, got ${ENTRIES.length}`);
const PLACEMENT_COUNT = GROUP_ORDER.reduce((sum, group) => sum + GROUP_LABELS[group].length, 0);
if (PLACEMENT_COUNT !== 114) throw new Error(`Formal iztro default star catalog expected 114 placements, got ${PLACEMENT_COUNT}`);

export const IZTRO_DEFAULT_108_STAR_CATALOG: FormalStarCatalog = Object.freeze({
  id: 'IZTRO-DEFAULT-108',
  version: '2.6.0-1',
  engine: 'iztro@2.6.0',
  algorithm: 'default',
  entries: ENTRIES,
  placementCount: 114,
  uniqueStarCount: 108,
  sourceNote: 'Pinned iztro@2.6.0 default algorithm: 14 major + 14 minor + 38 adjective + four 12-star cycles. Six labels occur in two groups, yielding 108 unique star labels across 114 placements.',
});

export function formalStarCatalogEntry(label: string): FormalStarCatalogEntry | undefined {
  return IZTRO_DEFAULT_108_STAR_CATALOG.entries.find((entry) => entry.label === label.trim());
}
